import { useContext, useEffect, useState } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  format,
  parseISO,
  differenceInYears,
  addMinutes,
  isWithinInterval,
} from 'date-fns'
// switch to this when calendar ids are fixed
import { apiClient } from 'utils/api/apiClient'

import Box from '@mui/material/Box'
import { Button, Select, Grid, IconButton, TextField } from '@mui/material'
// import Grid2 from '@mui/material/Grid2'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import Typography from '@mui/material/Typography'

import google from 'assets/images/logos/google_meets.png'
import teams from 'assets/images/logos/ms_teams.png'
import zoom from 'assets/images/logos/zoom.png'
import meeting from 'assets/images/icons/meeting-background.svg'

// Soft UI Dashboard PRO React components
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import OnboardingMultiOptionTile from '../OnboardingMultiOptionTile'
import { selectData } from 'layouts/onboarding'
import { callZapier } from 'utils/zapier/callZapier'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowBack, Close } from '@mui/icons-material'
import deskChair from 'assets/images/deskandchair.svg'
import { MenuItem } from '@mui/material'
import Banner from 'atoms/Banner'
import { Link } from 'react-router-dom'
import { useFormDebounce } from 'hooks/useFormDebounce'

const selectStyles = {
  '&>label': {
    fontSize: '14px',
    color: '#75787B',
    top: '-2px',
  },
  '&>label.Mui-focused': {
    fontSize: '1rem',
    color: '#2370ED',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.Mui-focused.Mui-error': {
    color: '#ea0606',
  },
  '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
    fontSize: '1rem',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.MuiFormLabel-filled.Mui-disabled': {
    top: 0,
    background:
      'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
  },
  '&>div.MuiInputBase-root': {
    padding: '0!important',
    borderRadius: '4px !important',
  },
  '&>div.MuiInputBase-root.Mui-disabled': {
    backgroundColor: '#e9ecef!important',
  },
  '&>div.MuiInputBase-root.Mui-focused': {
    boxShadow: '0 0 0 1.5px #2370ED',
  },
  '&>div>.MuiInputBase-input': {
    width: '100%!important',
    padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
    display: 'flex',
  },
  '&>div>.MuiInputBase-input>.MuiListItemIcon-root': {
    minWidth: 'unset',
  },
  '&>div>.MuiInputBase-input>.MuiListItemIcon-root>img': {
    height: '20px',
    width: 'auto',
    marginRight: '8px',
  },
  '&>div>.MuiInputBase-input>.MuiListItemText-root>span': {
    fontSize: '14px',
  },
  '&>div.MuiInputBase-root>svg': {
    display: 'block',
    height: '1.5em',
    width: '1.5em',
    top: 'calc(50% - 0.75em)',
  },
}

export const ScheduleMeeting = ({
  closeModal = () => console.log('Schedule Appointment'),
  isAdmin = false,
  isOnPageForm = false,
  isOnModal = false,
  questionContainerNext,
  questionContainerBack,
  prefetchedAvailability,
  portfolioReview = false,
}) => {
  // console.log('isAdmin', isAdmin)
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(false)
  const [data, setData] = useState(null)
  const [availableDates, setAvailableDates] = useState(null)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedPlatform, setSelectedPlatform] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [availableTimes, setAvailableTimes] = useState([])
  // Pagination
  const [open, setOpen] = useState(true)
  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState(1)
  const handleNext = () => {
    setDirection(1)
    setPage((prev) => Math.min(prev + 1, pages.length - 1))
  }
  const handleBack = () => {
    setDirection(-1)
    setPage((prev) => Math.max(prev - 1, 0))
  }
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const dateToYears = (startDate, endDate) => {
    return startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  }

  // Eastern Time Formatter
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  const fetchAvailability = async () => {
    const now = new Date()
    const endDate = new Date(now)
    endDate.setMonth(endDate.getMonth() + 2)
    const begin =
      now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate()
    const end =
      endDate.getFullYear() +
      '-' +
      (endDate.getMonth() + 1) +
      '-' +
      endDate.getDate()
    try {
      const calendarIds = []

      // IF HAS PRIMARY STRATEGIST
      if (
        intakeState.retirementStrategist !== '' &&
        intakeState.retirementStrategist
      ) {
        const strategistMatch = selectData.retirementStrategist.find(
          (s) =>
            s.value.toLowerCase().trim() ===
            intakeState.retirementStrategist.toLowerCase().trim()
        )

        if (strategistMatch) {
          // console.log('Times available for ' + strategistMatch.value)
          calendarIds.push(strategistMatch.id)
        } else {
          console.warn(
            'Strategist not found in selectData:',
            intakeState.retirementStrategist
          )
        }
      } else {
        if (dateToYears(intakeState?.dateOfBirth, new Date()) > 59) {
          //jpseph
          // console.log('times avaiable for joseph')

          calendarIds.push(selectData.retirementStrategist[3].id)
        } else if (
          intakeState.agency ===
            'u.s._agency_for_international_development_(usaid)' ||
          intakeState.agency === 'department_of_state_(dos)'
        ) {
          //dan
          // console.log('times avaiable for dan')
          calendarIds.push(selectData.retirementStrategist[1].id)
        } else {
          //dan & john
          // console.log('times avaiable for dan & john')

          calendarIds.push(selectData.retirementStrategist[0].id)
          calendarIds.push(selectData.retirementStrategist[1].id)
        }
      }

      const payload = {
        begin: begin,
        end: end,
        calendar_ids_to_check: calendarIds.join('%2C'),
      }

      const response = await fetch(
        'https://1p9cjflrkb.execute-api.us-east-2.amazonaws.com/api/users/availability',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )

      const data = await response.json()
      // console.log('data in scheduler:', data)

      // const { data } = await apiClient.post('/api/users/availability', {
      //   body: payload,
      // })
      setData(data)

      const availableDatesSet = new Set(
        data.availability.map((iso) => iso.split('T')[0])
      )
      setAvailableDates(availableDatesSet)
    } catch (e) {
      console.error('Error fetching availability:', e)
    }
  }

  // console.log(data.availability)

  // Initial fetch of availability data
  useEffect(() => {
    if (prefetchedAvailability && prefetchedAvailability.availability) {
      console.log('Using prefetched availability')
      setData(prefetchedAvailability)

      // Derive available dates
      const availableDatesSet = new Set(
        prefetchedAvailability.availability.map((iso) => iso.split('T')[0])
      )
      setAvailableDates(availableDatesSet)
    } else {
      console.log('Fetching availability')
      fetchAvailability()
    }
  }, [prefetchedAvailability])

  useEffect(() => {
    setSelectedTime(null) // Reset the selected time when date changes
  }, [selectedDate])

  const getAvailableTimes = (selectedDate) => {
    if (!data?.availability || !selectedDate) return []

    return data.availability
      .filter((isoString) => {
        const datePart = isoString.split('T')[0]
        return datePart === selectedDate
      })
      .map((isoString) => {
        const time = formatter.format(new Date(isoString))
        return { time: time, timeISO: isoString }
      })
  }

  // Update available times when the selected date changes
  useEffect(() => {
    if (!selectedDate || !data) return
    setAvailableTimes(getAvailableTimes(selectedDate))
  }, [selectedDate, data])

  const scheduleWith = intakeState?.retirementStrategist
    ? selectData.retirementStrategist.find(
        (s) => s.value === intakeState.retirementStrategist
      )?.sfId
    : dateToYears(intakeState?.dateOfBirth, new Date()) > 59
    ? selectData.retirementStrategist[3].sfId
    : // selectData.retirementStrategist[3].sfId +
    // ', ' +
    // selectData.retirementStrategist[2].sfId
    intakeState?.agency ===
        'u.s._agency_for_international_development_(usaid)' ||
      intakeState?.agency === 'department_of_state_(dos)'
    ? selectData.retirementStrategist[1].sfId
    : selectData.retirementStrategist[0].sfId +
      ', ' +
      selectData.retirementStrategist[1].sfId

  const sendApptToSchedule = () => {
    const eventToSchedule = {
      firstName: intakeState?.firstName || 'No First Name',
      lastName: intakeState?.lastName || 'No Last Name',
      email: intakeState?.email || 'No Email',
      phone: intakeState?.phone || 'No Phone',
      leadId: intakeState?.salesforceLeadID || 'No Lead ID',
      accountId: intakeState?.salesforceAccountID || 'No Account ID',
      scheduleWith: scheduleWith,
      selectedDate: format(new Date(selectedTime), 'MM/dd/yyyy') || '',
      selectedTime: formatter.format(new Date(selectedTime)) + ' EST' || '',
      dateTimeISO: selectedTime || '',
      selectedPlatform: selectedPlatform || '',
      meetingTypeWithduration:
        selectData.appointmentTypes.find(
          ({ value }) => value === selectedType
        ) || '',
      portfolioReview: portfolioReview,
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2ca74fr/',
      payload: {
        eventToSchedule: eventToSchedule,
      },
      notificationMessage: `Your Appointment on ${format(
        new Date(selectedTime),
        'MM/dd/yyyy'
      )} at ${formatter.format(
        new Date(selectedTime)
      )} EST is being scheduled!`,
      sendNotification: true,
    })
    setAppointmentConfirmed(true)
    updateIntakeStateAtKey({ appointmentScheduled: true })
    handleNext()
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        scheduledMeetingInApp: true,
      })
    )()
    // closeModal()
  }

  const pages = [
    <Grid
      key="1"
      item
      xs={12}
      mb={5}
      sx={{
        '& .MuiDayCalendar-weekDayLabel': {
          margin: '0px 0.5vw',
        },
        '& .MuiPickersDay-root': {
          fontWeight: 600,
          margin: '0px 0.5vw',
        },
        '& .MuiPickersDay-root.Mui-disabled': {
          fontWeight: 400,
        },
        '& .MuiPickersDay-root.Mui-selected': {
          backgroundColor: '#0671AD',
        },
        '& .MuiPickersDay-root.Mui-selected:active, & .MuiPickersDay-root.Mui-selected:focus, & .MuiPickersDay-root.Mui-selected:hover':
          {
            backgroundColor: '#045a8a',
          },
        '& .MuiPickersDay-root:hover': {
          backgroundColor: '#e6f0f6',
        },
        '& .MuiPickersCalendarHeader-labelContainer': {
          position: 'absolute',
          left: '50%',
          transform: 'translate(-50%)',
        },
        '& .MuiPickersArrowSwitcher-root': {
          justifyContent: 'space-between',
          width: '100%',
        },
      }}
    >
      {!isOnModal && (
        <>
          <Box>
            <Typography variant="headerTitle" sx={{ color: '#262627' }}>
              What day would you like to meet?
            </Typography>
          </Box>
          <Typography variant="body1" mt={3}>
            Select one below:
          </Typography>
        </>
      )}

      <DateCalendar
        sx={{ width: '100%' }}
        shouldDisableDate={(date) => {
          const today = new Date()
          today.setHours(0, 0, 0, 0) // Normalize today's date (remove time part)

          const twoMonthsLater = new Date()
          twoMonthsLater.setMonth(today.getMonth() + 2)

          const dateString = format(date, 'yyyy-MM-dd')

          return (
            date < today ||
            date > twoMonthsLater ||
            (availableDates && !availableDates.has(dateString))
          ) // Disable less than today, older than 2 months and dates with no availability
        }}
        value={selectedDate ? parseISO(selectedDate) : null}
        onChange={(newValue) => {
          setSelectedDate(format(newValue, 'yyyy-MM-dd'))

          setSelectedTime(null)
        }}
        views={['day']}
      />
      {selectedDate && (
        <>
          <Typography variant="body1" mb={1}>
            Select a time from the times available:
          </Typography>
          <Box mt={2}>
            {renderMuiFormField({
              key: selectedDate,
              autoFocus: false,
              label: 'Meeting Time',
              placeholder: 'Select a Meeting Timeslot (Eastern Time)',
              inputType: 'select',
              value: selectedTime,
              onChange: setSelectedTime,
              options: availableTimes.map((time) => ({
                value: time.timeISO,
                label: time.time + ' EST',
              })),
            })}
          </Box>
          {/* <TextField
            native
            sx={selectStyles}
            id={'schedule'}
            label={'Meeting Time'}
            placeholder={'Meeting Time'}
            // onChange={'onChange'}
            // onBlur={onBlur}
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            select
            fullWidth
          >
            {availableTimes.map((timeObj) => (
              <MenuItem key={timeObj.timeISO} value={timeObj.timeISO}>
                {timeObj.time} EST
              </MenuItem>
            ))}
          </TextField> */}
        </>
      )}
    </Grid>,
    <Grid key="2" item xs={12}>
      {!isOnModal ? (
        <>
          <Box mb={1}>
            <Typography variant="headerTitle" sx={{ color: '#262627' }}>
              Which platform would you prefer to meet with?
            </Typography>
          </Box>
          <Typography variant="body1" mt={3}>
            Please select one of the following:
          </Typography>
        </>
      ) : (
        <>
          <Box mb={1}>
            <Typography variant="body2Medium" sx={{ color: '#262627' }}>
              Which platform would you prefer to meet with?
            </Typography>
          </Box>
          <Typography variant="body1" mt={1}>
            Please select one of the following:
          </Typography>
        </>
      )}

      <Box>
        <OnboardingMultiOptionTile
          header={' '}
          helpText={''}
          subHeader={' '}
          expectedDataType={'string'}
          tileSize={{ xs: '4' }}
          onChange={(e) => {
            setSelectedPlatform(e)
          }}
          optionArray={selectData.meetingOptions.map((option) => ({
            optionLabel: option.label,
            optionValue: `${option.value}_retirement_consultation`,
            optionIcon: (
              <img
                src={
                  option.value === 'zoom'
                    ? zoom
                    : option.value === 'ms_teams'
                    ? teams
                    : option.value === 'google_meet'
                    ? google
                    : option.logo
                }
                alt={option.alt}
              />
            ),
          }))}
        />
        {!isOnPageForm ? (
          <Box justifyContent="flex-end" display="flex">
            <Button
              variant="parcoRounded"
              disabled={!selectedDate || !selectedTime || !selectedPlatform}
              onClick={sendApptToSchedule}
            >
              Schedule Meeting
            </Button>
          </Box>
        ) : (
          <>
            <Box justifyContent="space-between" display="flex">
              <Button
                variant="parcoRounded"
                color="outline"
                onClick={handleBack}
                disabled={!selectedDate}
              >
                Back
              </Button>
              <Button
                variant="parcoRounded"
                disabled={!selectedDate || !selectedTime || !selectedPlatform}
                onClick={sendApptToSchedule}
              >
                Schedule Meeting
              </Button>
            </Box>
            <Box justifyContent="flex-end" display="flex">
              <Button
                sx={{
                  fontSize: '14px',
                  color: '#A3a7AA',
                  textDecoration: 'underline',
                  textTransform: 'none',
                  fontWeight: 'normal',
                  '&:hover': { color: '#A3a7AA' },
                }}
                onClick={questionContainerNext}
              >
                Skip Scheduling
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Grid>,
    <Grid key="3" item xs={12}>
      <Box mb={4}>
        {!isOnModal ? (
          <>
            <Typography variant="headerTitle" sx={{ color: '#262627' }}>
              Your Retirement Consultation is scheduled!
            </Typography>
            <Typography variant="body1" mt={3} mb={4}>
              Your meeting details will be emailed to you shortly. To continue
              onboarding or access your dashboard we will first need you to
              finalize your account creation.
            </Typography>
          </>
        ) : (
          <>
            <Box mb={1}>
              <Typography variant="headerTitle" sx={{ color: '#262627' }}>
                Your Retirement Consultation is scheduled!
              </Typography>
            </Box>
          </>
        )}
        <Banner
          color="green"
          arrow={false}
          title="MEETING SCHEDULED"
          description={`${format(new Date(selectedTime), 'MMMM do')} 
          @ ${formatter.format(new Date(selectedTime))} EST`}
          content={
            <img
              style={{
                objectFit: 'cover',
                paddingTop: '16px',
                width: '100px',
              }}
              src={meeting}
            />
          }
        />
      </Box>
    </Grid>,
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {!isOnModal ? (
        <>
          <Box
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              display: `${!isOnPageForm ? 'flex' : 'none'}`,
              top: { xs: -8, sm: -64 },
              '@media (max-height:960px)': {
                top: -8,
              },
            }}
          >
            {page > 0 && (
              <IconButton
                onClick={handleBack}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: { xs: -8, sm: -16 },
                  color: '#fff',
                  zIndex: 1,
                  backgroundColor: '#000',
                  '&:hover': {
                    backgroundColor: '#000',
                  },
                  '@media (max-height:960px)': {
                    left: -8,
                  },
                }}
              >
                <ArrowBack />
              </IconButton>
            )}

            <IconButton
              onClick={closeModal}
              sx={{
                position: 'absolute',
                top: 0,
                right: { xs: -8, sm: -16 },
                color: '#fff',
                zIndex: 1,
                backgroundColor: '#000',
                '&:hover': {
                  backgroundColor: '#000',
                },
                '@media (max-height:960px)': {
                  right: -8,
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Grid
            sx={{ textAlign: 'left', overflow: 'hidden' }}
            px={isOnPageForm ? 0 : { xs: 0, sm: 4 }}
            pt={isOnPageForm ? 0 : 4}
            pb={isOnPageForm ? 0 : { xs: 2, sm: 4 }}
          >
            {/* <motion.div
          animate={{ height }}
          transition={{ duration: 0.3 }}
          style={{ overflow: 'hidden' }}
        > */}
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={page}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                style={{ position: 'relative', width: '100%' }}
              >
                {pages[page]}
                {page === 1 ? (
                  <></>
                ) : page < pages.length - 1 ? (
                  <>
                    {!isOnPageForm ? (
                      <Box justifyContent="flex-end" display="flex">
                        <Button
                          variant="parcoRounded"
                          onClick={handleNext}
                          disabled={!selectedDate || !selectedTime}
                        >
                          Next
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Box justifyContent="space-between" display="flex">
                          <Button
                            variant="parcoRounded"
                            color="outline"
                            onClick={questionContainerBack}
                          >
                            Back
                          </Button>
                          <Button
                            variant="parcoRounded"
                            onClick={handleNext}
                            disabled={!selectedDate || !selectedTime}
                          >
                            Continue to Schedule
                          </Button>
                        </Box>
                        <Box justifyContent="flex-end" display="flex">
                          <Button
                            sx={{
                              fontSize: '14px',
                              color: '#A3a7AA',
                              textDecoration: 'underline',
                              textTransform: 'none',
                              fontWeight: 'normal',
                              '&:hover': { color: '#A3a7AA' },
                            }}
                            onClick={questionContainerNext}
                          >
                            Skip Scheduling
                          </Button>
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {!isOnPageForm ? (
                      <Button variant="parcoRounded" onClick={closeModal}>
                        Close
                      </Button>
                    ) : (
                      <Box justifyContent="flex-end" display="flex">
                        {/* <Button
                      variant="parcoRounded"
                      onClick={questionContainerNext}
                    >
                      Create My Account
                    </Button> */}
                        {window.location.pathname === '/schedule' ? (
                          <Button
                            variant="parcoRounded"
                            onClick={questionContainerNext}
                          >
                            Continue Onboarding
                          </Button>
                        ) : (
                          <Link
                            to={
                              '/onboarding-full?onboardingStep=interstitialPickYourPath'
                            }
                          >
                            <Button variant="parcoRounded">
                              Create My Account
                            </Button>
                          </Link>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
            {/* </motion.div> */}
          </Grid>
        </>
      ) : (
        <>
          {pages[page]}

          {page === 1 ? (
            <></>
          ) : page < pages.length - 1 ? (
            <>
              {!isOnPageForm ? (
                <Box justifyContent="flex-end" display="flex">
                  <Button
                    variant="parcoRounded"
                    onClick={handleNext}
                    disabled={!selectedDate || !selectedTime}
                  >
                    Next
                  </Button>
                </Box>
              ) : (
                <>
                  <Box justifyContent="space-between" display="flex">
                    <Button
                      variant="parcoRounded"
                      color="outline"
                      onClick={questionContainerBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="parcoRounded"
                      onClick={handleNext}
                      disabled={!selectedDate || !selectedTime}
                    >
                      Continue to Schedule
                    </Button>
                  </Box>
                </>
              )}
            </>
          ) : (
            <>
              {!isOnPageForm ? (
                <>
                  {!isOnModal && (
                    <Button variant="parcoRounded" onClick={closeModal}>
                      Close
                    </Button>
                  )}
                </>
              ) : (
                <Box justifyContent="flex-end" display="flex">
                  {/* <Button
                      variant="parcoRounded"
                      onClick={questionContainerNext}
                    >
                      Create My Account
                    </Button> */}
                  <Link
                    to={
                      '/onboarding-full?onboardingStep=interstitialPickYourPath'
                    }
                  >
                    <Button variant="parcoRounded">Create My Account</Button>
                  </Link>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </LocalizationProvider>
  )
}
